import { create } from 'zustand'
import { authService } from '../services/api/authService'
import { queryClient } from '../lib/queryClient'
import useNotificationStore from './useNotificationStore'

const resetReactQueryCache = async () => {
  try {
    await queryClient.cancelQueries()
  } catch (error) {
    console.error('Error cancelling queries:', error)
  }

  try {
    queryClient.clear()
  } catch (error) {
    console.error('Error clearing query cache:', error)
  }
}

// Load user from localStorage on initialization
const loadUserFromStorage = () => {
  try {
    const storedUser = localStorage.getItem('user')
    const accessToken = localStorage.getItem('accessToken')
    const storedSubscription = localStorage.getItem('subscription')
    const storedHasActiveSubscription = localStorage.getItem('hasActiveSubscription')
    if (storedUser && accessToken) {
      return {
        user: JSON.parse(storedUser),
        subscription: storedSubscription ? JSON.parse(storedSubscription) : null,
        hasActiveSubscription: storedHasActiveSubscription
          ? JSON.parse(storedHasActiveSubscription)
          : false,
        isAuthenticated: true,
      }
    }
  } catch (error) {
    console.error('Error loading user from storage:', error)
  }
  return {
    user: null,
    subscription: null,
    hasActiveSubscription: false,
    isAuthenticated: false,
  }
}

const useAuthStore = create((set, get) => ({
  // Initial state from localStorage
  ...loadUserFromStorage(),

  // Actions
  setUser: (user) => {
    localStorage.setItem('user', JSON.stringify(user))
    set({ user })
  },
  setSubscription: (subscription) => {
    localStorage.setItem('subscription', JSON.stringify(subscription))
    set({ subscription })
  },
  setHasActiveSubscription: (hasActiveSubscription) => {
    localStorage.setItem('hasActiveSubscription', JSON.stringify(Boolean(hasActiveSubscription)))
    set({ hasActiveSubscription: Boolean(hasActiveSubscription) })
  },
  setAuthenticated: (isAuthenticated) => set({ isAuthenticated }),
  
  login: async (credentials) => {
    try {
      // Defensive: if a user logs in right after another user without a hard refresh,
      // make sure we don't reuse "fresh" cached queries from the previous session.
      await resetReactQueryCache()

      const response = await authService.login(credentials)
      
      // Store tokens and user data - API returns { user, tokens: { access: { token }, refresh: { token } } }
      if (response.tokens?.access?.token) {
        localStorage.setItem('accessToken', response.tokens.access.token)
      }
      if (response.tokens?.refresh?.token) {
        localStorage.setItem('refreshToken', response.tokens.refresh.token)
      }
      if (response.user) {
        localStorage.setItem('user', JSON.stringify(response.user))
        if (response.subscription) {
          localStorage.setItem('subscription', JSON.stringify(response.subscription))
        }
        if (typeof response.hasActiveSubscription !== 'undefined') {
          localStorage.setItem('hasActiveSubscription', JSON.stringify(Boolean(response.hasActiveSubscription)))
        }
        set({
          user: response.user,
          subscription: response.subscription || null,
          hasActiveSubscription: Boolean(response.hasActiveSubscription),
          isAuthenticated: true,
        })
      }

      return response
    } catch (error) {
      throw error
    }
  },

  logout: async () => {
    try {
      // Stop any in-flight requests that could race with token removal.
      await resetReactQueryCache()

      const refreshToken = localStorage.getItem('refreshToken')
      if (refreshToken) {
        await authService.logout(refreshToken)
      }
    } catch (error) {
      console.error('Error during logout:', error)
    } finally {
      // Disconnect the notifications socket and clear in-memory notification state
      useNotificationStore.getState().reset()

      // Clear all stored data
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('user')
      localStorage.removeItem('subscription')
      localStorage.removeItem('hasActiveSubscription')

      // Ensure no user-scoped data remains in memory after logout.
      await resetReactQueryCache()

      set({
        user: null,
        subscription: null,
        hasActiveSubscription: false,
        isAuthenticated: false,
      })
    }
  },

  getMe: async () => {
    try {
      const response = await authService.getMe()
      if (response.user) {
        localStorage.setItem('user', JSON.stringify(response.user))
        if (response.subscription) {
          localStorage.setItem('subscription', JSON.stringify(response.subscription))
        }
        if (typeof response.hasActiveSubscription !== 'undefined') {
          localStorage.setItem('hasActiveSubscription', JSON.stringify(Boolean(response.hasActiveSubscription)))
        }
        set({
          user: response.user,
          subscription: response.subscription || null,
          hasActiveSubscription: Boolean(response.hasActiveSubscription),
        })
      }
      return response
    } catch (error) {
      throw error
    }
  },
}))

export default useAuthStore
